/**
 * Adapter to convert field registry to MUI DataGrid columns
 * for the Products Row Grouping POC
 */

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import {
  FLAT_PRODUCT_FIELDS,
  GROUPABLE_FIELD_KEYS,
} from './ProductsRowGroupingDataGrid.fields';
import { FlatProduct as FlatProductRow } from '../services/api/domains/products/types';
import { getProductDisplayName } from '../utils/productNameHelpers';
import {
  renderBooleanCell,
  renderStringArrayCell,
  renderHtmlCell,
} from '../shared/utils/cellRenderers';
import { getGridFilterOperatorsForType } from '../utils/fieldOperators';
import { formatDmfType } from '../utils/dmfTypeLabels';
import DisplayNameSourceIndicator from './DisplayNameSourceIndicator';
import SynonymsCell from './SynonymsCell';
import {
  DisplayNameSource,
  DISPLAY_NAME_SOURCE,
} from '../services/api/domains/products/types';

interface ToDataGridColumnsOptions {
  /** "View all" button inside the dedicated synonyms cell (Seq 4.4,
   * PIV-033) -- opens the product modal deep-linked to the CAS Registry
   * panel. Omitted entirely (no button rendered) if not provided. */
  onViewAllSynonyms?: (product: FlatProductRow) => void;
}

/**
 * Convert field registry to DataGrid column definitions
 */
export function toDataGridColumns(
  options: ToDataGridColumnsOptions = {}
): GridColDef<FlatProductRow>[] {
  const { onViewAllSynonyms } = options;
  return FLAT_PRODUCT_FIELDS.map(field => {
    const colDef: GridColDef<FlatProductRow> = {
      field: field.key as string,
      headerName: field.label,
      width: getColumnWidth(field.key as string),
      sortable: field.isSortable ?? false,
      filterable: field.isFilterable ?? false,
      hideable: true,
    };

    // Mark groupable columns
    if (
      GROUPABLE_FIELD_KEYS.includes(
        field.key as (typeof GROUPABLE_FIELD_KEYS)[number]
      )
    ) {
      colDef.groupable = true;
    }

    // Special handling for preferred_product_name — reads the server-side
    // materialized display_name column via getProductDisplayName.
    if (field.key === 'preferred_product_name') {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const displayName = getProductDisplayName(params.row);
        return (
          <DisplayNameSourceIndicator
            displayNameSource={
              params.row.display_name_source as DisplayNameSource | null
            }
            sourceLabel={
              params.row.display_name_source ===
              DISPLAY_NAME_SOURCE.MANUFACTURER
                ? (params.row.manufacturer_name ?? undefined)
                : undefined
            }>
            {displayName}
          </DisplayNameSourceIndicator>
        );
      };
      // groupingValueGetter controls the key used for tree construction —
      // ensures rows with a blank preferred_product_name still group instead
      // of being skipped to the next grouping level.
      colDef.groupingValueGetter = (value, row) =>
        getProductDisplayName(row as FlatProductRow);
    }

    // Dedicated synonyms cell (Seq 4.4, PIV-033) — NOT the shared
    // ExpandableListCell/renderStringArrayCell path below: the server caps
    // `synonyms` at 10 and reports the real count via
    // `synonym_total_count`, so "View all" visibility must be driven by
    // that server total, not by re-deriving a "+N more" count from the
    // (already-capped) array length the way every other array field does.
    if (field.key === 'synonyms') {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => (
        <SynonymsCell
          synonyms={params.row.synonyms || []}
          totalCount={
            params.row.synonym_total_count ?? params.row.synonyms?.length ?? 0
          }
          onViewAll={
            onViewAllSynonyms ? () => onViewAllSynonyms(params.row) : undefined
          }
        />
      );
      return colDef;
    }

    // Multi-value array fields (issue #585 Task 8/10.1) —
    // orange_book_ingredient, ndc_active_ingredient, manufacturer,
    // replaced_rns, dmf_number, dmf_type (synonyms handled above via its
    // own dedicated cell). Rendered via the same ExpandableListCell "+N
    // more" pattern already proven for relation fields elsewhere; empty
    // arrays render a plain '-' (no error, no stray "+0 more" —
    // ExpandableListCell's own existing behavior).
    if (field.dataType === 'array') {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        // String()-normalize every element -- dmf_number comes from the
        // backend as ddmf.dmf_number (SQL INT, unconverted), so the raw
        // array is number[] at runtime despite the string[] cast below.
        const items = (
          (params.row[field.key as keyof FlatProductRow] as
            (string | number)[] | undefined) || []
        ).map(String);
        // dmf_type displays "II - API" per element, not the raw 'II'/'IV'
        // code (issue #585 Task 10.1, Open Question 5; format aligned to
        // the standard "[code] - [label]" pattern, 2026-09-23 follow-up).
        const displayItems =
          field.key === 'dmf_type' ? items.map(formatDmfType) : items;
        return renderStringArrayCell(
          displayItems,
          field.renderConfig?.maxDisplayItems,
          field.renderConfig?.formatAs === 'html'
        );
      };
    }

    // Scalar HTML content (e.g. molecular_formula, cas_official_* — CAS-
    // source names/formulas can carry markup like <em> or <span
    // class="text-smallcaps">). preferred_product_name is excluded — it
    // already has its own renderCell above for display-name resolution.
    if (
      field.dataType === 'string' &&
      field.renderConfig?.formatAs === 'html' &&
      field.key !== 'preferred_product_name'
    ) {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const value = params.row[field.key as keyof FlatProductRow];
        if (!value) return '';
        return renderHtmlCell(
          value as string,
          field.renderConfig?.maxDisplayLength
        );
      };
    }

    // Boolean formatting
    if (field.dataType === 'boolean') {
      colDef.type = 'boolean';
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const value = params.row[field.key as keyof FlatProductRow];
        return renderBooleanCell(value);
      };
    }

    // Explicit filter operators for all filterable non-boolean columns.
    // Overrides MUI's default operator set so only backend-supported operators
    // (including isEmpty / isNotEmpty) appear — no MUI-only operators like
    // doesNotContain, startsWith, isAnyOf which the backend cannot process.
    if (field.isFilterable && field.dataType !== 'boolean') {
      colDef.filterOperators = getGridFilterOperatorsForType(
        field.dataType as 'string' | 'number' | 'date'
      );
    }

    // NDC linked group header formatting
    // renderCell applies to data cells only; group headers use valueFormatter
    if (field.key === 'is_ndc_linked') {
      colDef.valueFormatter = (value: boolean | null | undefined) => {
        if (value === null || value === undefined) return '—';
        return value ? '✓ NDC Linked' : '✗ Not NDC Linked';
      };
    }

    // Date formatting — deliberately NOT colDef.type = 'date': the API
    // returns an ISO string, never converted to a real Date object client-side
    // (see client/src/services/api/domains/products/service.ts), and MUI's
    // date column type throws if fed a non-Date value. Format the raw string
    // directly instead, matching the other domain grid adapters (e.g.
    // client/src/domains/manufacturers/adapters/dataGridAdapter.tsx).
    if (field.dataType === 'date') {
      colDef.renderCell = (params: GridRenderCellParams<FlatProductRow>) => {
        const value = params.row[field.key as keyof FlatProductRow];
        return value ? new Date(value as string).toLocaleString() : '';
      };
    }

    return colDef;
  });
}

/**
 * Get default column widths based on field type
 */
function getColumnWidth(key: string): number {
  const widthMap: Record<string, number> = {
    cas_official_common_chemistry: 250,
    cas_official_pubchem: 250,
    cas_official_fda_gsrs: 250,
    preferred_product_name: 250,
    cas_number: 150,
    is_cas_verified: 120,
    is_fda_linked: 120,
    is_ndc_linked: 120,
    is_dmf_linked: 120,
    dmf_number: 200,
    dmf_type: 200,
    comments: 250,
    molecular_formula: 180,
    molecular_mass: 150,
    note: 300,
    uri: 200,
    inchi: 250,
    inchi_key: 200,
    smile: 250,
    canonical_smile: 250,
    manufacturer_name: 200,
    manufacturing_status: 180,
    orange_book_ingredient: 250,
    ndc_active_ingredient: 250,
    manufacturer: 250,
    synonyms: 300,
    replaced_rns: 250,
    facility_name: 200,
    country_code: 120,
    facility_status: 150,
    grade_name: 150,
    therapeutic_area_name: 200,
    created_date: 180,
    modified_date: 180,
  };

  return widthMap[key] ?? 150;
}

/**
 * Get column visibility model based on current grouping
 * Hides columns that are actively used for grouping
 */
export function getColumnVisibilityModel(
  groupingModel: string[]
): Record<string, boolean> {
  const visibilityModel: Record<string, boolean> = {};

  // Hide columns that are used for grouping
  groupingModel.forEach(fieldKey => {
    visibilityModel[fieldKey] = false;
  });

  return visibilityModel;
}

/**
 * Get the field keys used as the Rule 5 leaf-row-dedup key: user-visible
 * columns unioned with the active grouping dimensions.
 *
 * Grouping dimensions must stay in the key even though they're hidden as
 * flat columns (they render via the group tree instead) -- two leaves that
 * differ only in a grouping dimension (e.g. manufacturer_name) are not
 * duplicates for grouping purposes, so hiding that column must never merge
 * them. See VS-2/VS-3/VS-6 in docs/features/datagrid-collapsing-scenarios.md
 * and the leaf-rows-only scope clause for Rule 5 in
 * docs/features/datagrid-improvements.md.
 *
 * Deliberately takes the raw user column-visibility state, NOT the
 * grouping-merged `columnVisibilityModel` -- merging grouping-forced-hidden
 * fields into this key is exactly the bug this function fixes.
 */
export function getDedupFieldKeys(
  columnVisibilityState: Record<string, boolean>,
  groupingModel: string[]
): string[] {
  const groupingFieldSet = new Set(groupingModel);
  return FLAT_PRODUCT_FIELDS.filter(
    field =>
      columnVisibilityState[field.key as string] !== false ||
      groupingFieldSet.has(field.key as string)
  ).map(field => field.key as string);
}

/**
 * Get all column keys
 */
export function getAllColumnKeys(): string[] {
  return FLAT_PRODUCT_FIELDS.map(field => field.key as string);
}
